import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  CheckboxAnswer,
  CheckboxItem,
  MatrixAnswer,
  MatrixConfig,
  QuestionnaireQuestion,
  QuestionnaireSection,
  RadioItem,
  Submission
} from '../../models/questionnaire.model'
import { evaluateShowWhen } from '../show-when.util'
import { escapeHtml } from '@common/utils/sanitize'
import { environment } from '../../../../../environments/environment'

@Component({
  selector: 'app-questionnaire-review',
  templateUrl: './questionnaire-review.component.html',
  styleUrls: ['./questionnaire-review.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class QuestionnaireReviewComponent implements OnChanges {
  @Input() questionnaire: QuestionnaireSection[] = []
  @Input() submission: Submission | null = null
  @Input() readOnly: boolean = false
  @Input() hideHeader: boolean = false
  @Input() periodLabel: string = ''
  @Output() editSection = new EventEmitter<number>()

  apiBaseUrl = environment.apiBaseUrl

  private _answeredByKeyBySection: Map<number, Record<string, string>> | null = null
  private _sectionByQuestionId: Map<number, number> | null = null

  ngOnChanges(): void {
    this._answeredByKeyBySection = null
    this._sectionByQuestionId = null
  }

  private buildIndexes(): void {
    this._answeredByKeyBySection = new Map()
    this._sectionByQuestionId = new Map()
    const collect = (q: QuestionnaireQuestion, sectionId: number) => {
      this._sectionByQuestionId!.set(q.id, sectionId)
      if (q.children?.length) {
        for (const c of q.children) collect(c, sectionId)
      }
    }
    for (const section of this.questionnaire) {
      this._answeredByKeyBySection.set(section.id, {})
      for (const q of section.questions) collect(q, section.id)
    }
    if (this.submission?.answers) {
      for (const a of this.submission.answers) {
        if (!a.answer) continue
        const sectionId = this._sectionByQuestionId.get(a.questionId)
        if (sectionId === undefined) continue
        const sectionMap = this._answeredByKeyBySection.get(sectionId)!
        const question = this.findQuestionById(a.questionId)
        if (question) sectionMap[question.questionKey] = a.answer
      }
    }
  }

  private findQuestionById(id: number): QuestionnaireQuestion | undefined {
    const walk = (q: QuestionnaireQuestion): QuestionnaireQuestion | undefined => {
      if (q.id === id) return q
      for (const c of q.children || []) {
        const found = walk(c)
        if (found) return found
      }
      return undefined
    }
    for (const section of this.questionnaire) {
      for (const q of section.questions) {
        const found = walk(q)
        if (found) return found
      }
    }
    return undefined
  }

  private answersForQuestion(question: QuestionnaireQuestion): Record<string, string> {
    if (!this._answeredByKeyBySection) this.buildIndexes()
    const sectionId = this._sectionByQuestionId!.get(question.id)
    if (sectionId === undefined) return {}
    return this._answeredByKeyBySection!.get(sectionId) || {}
  }

  isQuestionVisible(question: QuestionnaireQuestion): boolean {
    if (!question.showWhen) return true
    return evaluateShowWhen(question.showWhen, this.answersForQuestion(question))
  }

  getRawAnswer(questionId: number): string | null {
    if (!this.submission?.answers) return null
    const answer = this.submission.answers.find(a => a.questionId === questionId)
    return answer?.answer || null
  }

  getAnswer(questionId: number): string | null {
    if (!this.submission?.answers) return null
    const answer = this.submission.answers.find(a => a.questionId === questionId)
    return answer?.answerMapped || answer?.answer || null
  }

  getFormattedAnswer(question: QuestionnaireQuestion): string | null {
    const raw = this.getRawAnswer(question.id)
    if (!raw) return null

    if (question.type === 'checkbox') {
      return this.formatCheckboxAnswer(raw, question.items as CheckboxItem[])
    }

    if (question.type === 'matrix') {
      return this.formatMatrixAnswer(raw, question.items as MatrixConfig)
    }

    if (question.type === 'radio' && question.items) {
      return this.formatRadioAnswer(raw, question.items as RadioItem[])
    }

    return this.getAnswer(question.id)
  }

  private formatRadioAnswer(raw: string, items: RadioItem[]): string | null {
    const match = (items || []).find(i => (i.value ?? i.label) === raw)
    if (match) return escapeHtml(match.label)
    if (raw === '[object Object]') return null
    return escapeHtml(raw)
  }

  private formatCheckboxAnswer(raw: string, items: CheckboxItem[]): string {
    try {
      const parsed: CheckboxAnswer = JSON.parse(raw)
      if (!parsed.selected?.length) return null

      const itemMap = new Map((items || []).map((i) => [i.value, i]))
      const lines = parsed.selected.map((val) => {
        const item = itemMap.get(val)
        const label = escapeHtml(item?.label || val)
        const specify = parsed.specifyText?.[val]
        return specify ? `<li>${label}: ${escapeHtml(specify)}</li>` : `<li>${label}</li>`
      })
      return `<ul>${lines.join('')}</ul>`
    } catch {
      return escapeHtml(raw)
    }
  }

  private formatMatrixAnswer(raw: string, config: MatrixConfig): string {
    try {
      const parsed = JSON.parse(raw)
      if (!config?.rows?.length || !config?.columns?.length) return raw

      let selections: Record<string, string>
      let specifyText: Record<string, string> = {}

      if (parsed.selections) {
        selections = parsed.selections
        specifyText = parsed.specifyText || {}
      } else {
        selections = parsed
      }

      let html = '<table class="table is-bordered is-narrow is-fullwidth"><thead><tr><th></th>'
      for (const col of config.columns) {
        html += `<th class="has-text-centered">${escapeHtml(col.label)}</th>`
      }
      html += '</tr></thead><tbody>'
      for (const row of config.rows) {
        const specify = row.hasSpecify && specifyText[row.key]
          ? `: <em>${escapeHtml(specifyText[row.key])}</em>`
          : ''
        html += `<tr><td>${escapeHtml(row.label)}${specify}</td>`
        for (const col of config.columns) {
          const selected = selections[row.key] === col.key
          html += `<td class="has-text-centered">${selected ? '&#10003;' : ''}</td>`
        }
        html += '</tr>'
      }
      html += '</tbody></table>'
      return html
    } catch {
      return escapeHtml(raw)
    }
  }

  getToolReferences(questionId: number): { id: number; name: string; status: string }[] {
    return this.submission?.toolReferences?.[questionId] ?? []
  }

  getMetricReferences(questionId: number): { id: number; name: string; status: string }[] {
    return this.submission?.metricReferences?.[questionId] ?? []
  }

  hasAnswer(questionId: number): boolean {
    return !!this.getRawAnswer(questionId)
  }

  formatPrefix(prefix: string | null): string {
    return prefix ? prefix.replace(/\.+$/, '') + '. ' : ''
  }

  onEditSection(sectionNumber: number): void {
    this.editSection.emit(sectionNumber)
  }
}
